import React, { useState, useEffect } from "react";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  updateDoc,
  increment,
  Timestamp,
  arrayUnion,
  query,
  where,
  getDocs,
} from "firebase/firestore";
import { db, auth } from "./firebase";
import "./QuickPayment.css";

const roundMoney = (amount) => Math.round(amount * 100) / 100;

// Helper function to get effective admin fee (checking overrides first)
const getEffectiveAdminFee = (student) => {
  const feeOverrides = student.feeOverrides || [];
  
  // Check for admin fee override
  const adminOverride = feeOverrides.find(
    o => o.overrideType === 'admin' || o.overrideType === 'both'
  );
  
  // Use override if it exists, otherwise use base admin fee
  return adminOverride?.adminFee ?? (student.adminFee || 0);
};



// Helper function to calculate which terms student has attended (for display/owing)
const calculateTermsAttended = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) return [];
  
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const currentYear = today.getFullYear();
  const termsAttended = [];
  
  for (let i = 1; i <= 4; i++) {
    const termData = termStructure.termDates[`term${i}`];
    if (!termData || !termData.start) continue;
    
    const [startMonth, startDay] = termData.start.split('-').map(Number);
    const [endMonth, endDay] = termData.end.split('-').map(Number);
    
    const termStart = new Date(currentYear, startMonth - 1, startDay);
    termStart.setHours(0, 0, 0, 0);
    
    const termEnd = new Date(currentYear, endMonth - 1, endDay);
    termEnd.setHours(23, 59, 59, 999);
    
    if (startDate <= termEnd && today >= termStart) {
      termsAttended.push(i);
    }
  }
  
  return termsAttended;
};

// Helper function to get ALL terms a student can pay for (allows paying ahead)
const getPayableTerms = (studentStartDate, termStructure) => {
  if (!termStructure || !termStructure.termDates) return [];
  
  const startDate = studentStartDate.toDate ? studentStartDate.toDate() : new Date(studentStartDate);
  startDate.setHours(0, 0, 0, 0);
  
  const currentYear = new Date().getFullYear();
  const payableTerms = [];
  
  for (let i = 1; i <= 4; i++) {
    const termData = termStructure.termDates[`term${i}`];
    if (!termData || !termData.start) continue;
    
    const [endMonth, endDay] = termData.end.split('-').map(Number);
    
    const termEnd = new Date(currentYear, endMonth - 1, endDay);
    termEnd.setHours(23, 59, 59, 999);
    
    // Student can pay for ANY term they're enrolled in (started before term ends)
    // This allows paying ahead for future terms
    if (startDate <= termEnd) {
      payableTerms.push(i);
    }
  }
  
  return payableTerms;
};

// Helper function to allocate payment across admin and terms
const allocatePayment = (student, adminAmount, termAmount, termStructure) => {
  const allocations = [];
  let remaining = termAmount;
  
  // STEP 1: Admin fee
  if (adminAmount > 0) {
    const currentAdminPaid = student.termPayments?.admin || 0;
    const adminFee = getEffectiveAdminFee(student);
    
    // Only validate if there's an admin fee to pay
    if (adminFee > 0) {
      if (currentAdminPaid >= adminFee) {
        throw new Error(`Admin fee already paid (R${currentAdminPaid.toFixed(2)})`);
      }
      
      if (currentAdminPaid + adminAmount > adminFee) {
        throw new Error(
          `Admin fee overpayment not allowed.\n` +
          `Already paid: R${currentAdminPaid.toFixed(2)}\n` +
          `Admin fee: R${adminFee.toFixed(2)}\n` +
          `Maximum you can pay: R${(adminFee - currentAdminPaid).toFixed(2)}`
        );
      }
    } else {
      // No admin fee set for this student
      throw new Error(`No admin fee set for this student. Cannot allocate R${adminAmount.toFixed(2)} to admin fee.`);
    }
    
    allocations.push({ target: 'admin', amount: adminAmount });
  }
  
  // STEP 2: Term fees - use getPayableTerms to allow paying ahead
  if (remaining > 0) {
    const payableTerms = getPayableTerms(student.startDate, termStructure);
    const standardFee = student.feeStructure?.standardFee || 0;
    
    for (const termNum of payableTerms) {
      if (remaining <= 0) break;
      
      const termKey = `term${termNum}`;
      const alreadyPaid = student.termPayments?.[termKey] || 0;
      
      // Check for fee override for this specific term
      let termFee = standardFee;
      if (student.feeOverrides && Array.isArray(student.feeOverrides)) {
        const override = student.feeOverrides.find(
          o => o.termNumber === termNum && o.overrideType === 'term'
        );
        if (override && override.termFee !== undefined) {
          termFee = override.termFee;
        }
      }
      
      const stillOwes = roundMoney(termFee - alreadyPaid);
      
      if (stillOwes > 0) {
        const amountForThisTerm = roundMoney(Math.min(remaining, stillOwes));
        allocations.push({ target: termKey, amount: amountForThisTerm });
        remaining = roundMoney(remaining - amountForThisTerm);
      }
    }
    
    // STEP 3: Credit
    if (remaining > 0) {
      allocations.push({ target: 'credit', amount: roundMoney(remaining) });
    }
  }
  
  return allocations;
};

function QuickPayment({
  onClose,
  preSelectedStudentId = null,
  onReturnToSource,
}) {
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  
  // NEW: Student search state
  const [searchMode, setSearchMode] = useState(!preSelectedStudentId);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const [paymentData, setPaymentData] = useState({
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethod: "EFT",
    reference: "",
    adminFeeAmount: "0",
    termFeeAmount: "0",
  });

  useEffect(() => {
    if (preSelectedStudentId) {
      loadStudent(preSelectedStudentId);
    } else {
      setLoading(false);
    }
  }, [preSelectedStudentId]);

  // Debounce search - wait 300ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchQuery.length >= 2) {
        performSearch(searchQuery);
      } else {
        setSearchResults([]);
        setSearching(false);
      }
    }, 300);

    // Cleanup timer if searchQuery changes before 300ms
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // NEW: Search students function
  const performSearch = async (searchTerm) => {
    console.log("Starting search for:", searchTerm);
    setSearching(true);
    
    try {
      const user = auth.currentUser;
      if (!user) {
        console.error("No user logged in");
        setSearching(false);
        return;
      }

      const franchiseeRef = doc(db, "Franchisees", user.uid);

      const studentsQuery = query(
        collection(db, "students"),
        where("franchiseeRef", "==", franchiseeRef),
        where("isActive", "==", true)
      );

      const snapshot = await getDocs(studentsQuery);
      
      const allStudents = [];
      const searchLower = searchTerm.toLowerCase().trim();

      // Process all students
      for (const docSnap of snapshot.docs) {
        const data = docSnap.data();
        const studentName = (data.studentName || "").toLowerCase();
        const accountNumber = (data.accountNumber || "").toLowerCase();

        if (
          studentName.includes(searchLower) ||
          accountNumber.includes(searchLower)
        ) {
          const studentData = {
            id: docSnap.id,
            ...data,
            studentName: data.studentName,
            accountNumber: data.accountNumber,
          };

          // Calculate admin fee owing for display (check overrides first)
          const adminFee = getEffectiveAdminFee(data);
          const adminPaid = data.termPayments?.admin || 0;
          const adminOwing = Math.max(0, adminFee - adminPaid);
          studentData.adminFeeOwing = adminOwing;

          // Calculate term fee owing from balance
          const balance = data.balance || 0;
          let termOwing = 0;
          if (balance < 0) {
            termOwing = Math.max(0, Math.abs(balance) - adminOwing);
          }
          studentData.termFeesOwing = termOwing;

          // Load school name if available
          if (data.schoolRef) {
            try {
              const schoolDoc = await getDoc(data.schoolRef);
              if (schoolDoc.exists()) {
                studentData.schoolName = schoolDoc.data().schoolName;
              } else {
                studentData.schoolName = "Unknown School";
              }
            } catch (err) {
              console.error("Error loading school:", err);
              studentData.schoolName = "Unknown School";
            }
          } else {
            studentData.schoolName = "No School";
          }

          allStudents.push(studentData);
        }
      }

      setSearchResults(allStudents.slice(0, 10));
    } catch (error) {
      console.error("Error searching students:", error);
      alert("Error searching students: " + error.message);
    }
    setSearching(false);
  };

  // NEW: Select student from search results
  const selectStudent = (studentId) => {
    setSearchMode(false);
    setSearchQuery("");
    setSearchResults([]);
    loadStudent(studentId);
  };

  const loadStudent = async (studentId) => {
    setLoading(true);
    try {
      const studentDoc = await getDoc(doc(db, "students", studentId));
      
      if (!studentDoc.exists()) {
        alert("Student not found");
        if (preSelectedStudentId) {
          onClose();
        } else {
          setSearchMode(true);
          setLoading(false);
        }
        return;
      }

      const studentData = {
        id: studentDoc.id,
        ...studentDoc.data(),
      };

      // Load school name
      if (studentData.schoolRef) {
        const schoolDoc = await getDoc(studentData.schoolRef);
        if (schoolDoc.exists()) {
          studentData.schoolName = schoolDoc.data().schoolName;
        }
      }

      // Calculate owing amounts - PROPERLY calculate from actual fields
      // Admin fee owing = effective admin fee (with overrides) - what's been paid (from termPayments.admin)
      const adminFee = getEffectiveAdminFee(studentData);
      const adminPaid = studentData.termPayments?.admin || 0;
      const adminOwing = Math.max(0, adminFee - adminPaid);
      
      // Term fees owing - calculate from balance field
      // Balance is negative when student owes money
      const balance = studentData.balance || 0;
      let termOwing = 0;
      if (balance < 0) {
        // Total owing is absolute value of balance
        // Term owing = total owing - admin owing
        termOwing = Math.max(0, Math.abs(balance) - adminOwing);
      }
      
      // Set the calculated values
      studentData.adminFeeOwing = adminOwing;
      studentData.termFeesOwing = termOwing;
      studentData.totalOwing = termOwing + adminOwing;
      studentData.termFeesPaid = studentData.termFeesPaid || 0;
      studentData.adminFeePaid = adminPaid;

      setStudent(studentData);
      setLoading(false);
    } catch (error) {
      console.error("Error loading student:", error);
      alert("Error loading student data");
      if (preSelectedStudentId) {
        onClose();
      } else {
        setSearchMode(true);
        setLoading(false);
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPaymentData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const formatCurrency = (amount) => {
    return `R${parseFloat(amount || 0).toFixed(2)}`;
  };

  const formatDate = (date) => {
    if (!date) return "N/A";
    if (date.toDate) return date.toDate().toLocaleDateString('en-GB');
    return new Date(date).toLocaleDateString('en-GB');
  };

  const getTotalPayment = () => {
  const admin = parseFloat(paymentData.adminFeeAmount) || 0;
  const term = parseFloat(paymentData.termFeeAmount) || 0;
  return roundMoney(admin + term);
};

  const getNewBalance = () => {
    return (student?.totalOwing || 0) - getTotalPayment();
  };

  const handleSaveAndFinish = async (e) => {
    e.preventDefault();
    
    const totalPayment = getTotalPayment();
    const adminAmount = roundMoney(parseFloat(paymentData.adminFeeAmount) || 0);
    const termAmount = roundMoney(parseFloat(paymentData.termFeeAmount) || 0);

    
    if (totalPayment <= 0) {
      alert("Please enter a payment amount");
      return;
    }

    if (!window.confirm(`Record payment of ${formatCurrency(totalPayment)}?`)) {
      return;
    }

    setSaving(true);

    try {
      const user = auth.currentUser;
      const franchiseeRef = doc(db, "Franchisees", user.uid);

      // Get term structure for allocation
      const schoolDoc = await getDoc(student.schoolRef);
      const schoolData = schoolDoc.data();
      let termStructure = null;
      if (schoolData.termStructureRef) {
        const termStructureDoc = await getDoc(schoolData.termStructureRef);
        if (termStructureDoc.exists()) {
          termStructure = termStructureDoc.data();
        }
      }

      // Calculate payment allocations
      let allocations;
      try {
        allocations = allocatePayment(student, adminAmount, termAmount, termStructure);
      } catch (error) {
        alert(error.message);
        setSaving(false);
        return;
      }

      // Build update object
      const updates = {};
      
      // Apply allocations
      for (const allocation of allocations) {
        if (allocation.target === 'admin') {
          updates['termPayments.admin'] = increment(allocation.amount);
        } else if (allocation.target === 'credit') {
          updates.creditBalance = increment(allocation.amount);
        } else {
          updates[`termPayments.${allocation.target}`] = increment(allocation.amount);
        }
      }
      
      // Update totals
      updates.totalPaid = increment(totalPayment);
      updates.lastPaymentDate = Timestamp.fromDate(new Date(paymentData.paymentDate));
      updates.updatedAt = Timestamp.now();
      
      // Recalculate balance
      const newTotalPaid = roundMoney((student.totalPaid || 0) + totalPayment);
      const totalExpected = student.totalExpected || 0;
      updates.balance = roundMoney(newTotalPaid - totalExpected);
      
      // Update payment status
      if (updates.balance >= 0) {
        updates.paymentStatus = 'paid';
      } else if (updates.balance < 0 && newTotalPaid > 0) {
        updates.paymentStatus = 'partial';
      } else {
        updates.paymentStatus = 'outstanding';
      }
      
      // Add to payment history
      const paymentRecord = {
        date: Timestamp.fromDate(new Date(paymentData.paymentDate)),
        adminAmount: adminAmount,
        termAmount: termAmount,
        method: paymentData.paymentMethod,
        reference: paymentData.reference || "",
        recordedBy: user.email,
        recordedAt: Timestamp.now()
      };
      
      if (allocations.length === 1) {
        paymentRecord.allocatedTo = allocations[0].target;
      } else {
        paymentRecord.allocations = allocations;
      }
      
      updates.paymentHistory = arrayUnion(paymentRecord);

      // Create payment record
      await addDoc(collection(db, "payments"), {
        studentRef: doc(db, "students", student.id),
        franchiseeRef: franchiseeRef,
        paymentDate: Timestamp.fromDate(new Date(paymentData.paymentDate)),
        paymentMethod: paymentData.paymentMethod,
        reference: paymentData.reference || "",
        adminFeeAmount: adminAmount,
        termFeeAmount: termAmount,
        totalAmount: totalPayment,
        allocations: allocations,
        notes: `Quick Payment - ${student.studentName}`,
        createdAt: Timestamp.now(),
        createdBy: user.email,
      });

      // Update student document
      await updateDoc(doc(db, "students", student.id), updates);

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);

      setTimeout(() => {
        if (onReturnToSource) {
          onReturnToSource();
        } else {
          onClose();
        }
      }, 1500);

    } catch (error) {
      console.error("Error saving payment:", error);
      alert("Error saving payment: " + error.message);
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="quick-payment-container">
        <div className="loading">Loading student data...</div>
      </div>
    );
  }

  // Student search UI
  if (searchMode && !student) {
    return (
      <div className="quick-payment-container">
        <div className="quick-payment-form">
          <div className="form-header">
            <div>
              <h1>{'\u{1F4B5}'} Quick Payment</h1>
              <p className="header-subtitle">Search for a student</p>
            </div>
            <button onClick={onClose} className="close-button">
              {'\u2715'}
            </button>
          </div>

          <div className="student-search-section">
            <div className="search-input-wrapper">
              <input
                type="text"
                className="search-input"
                placeholder="Type student name or account number..."
                value={searchQuery}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchQuery(value);
                  // Search will be triggered by useEffect with debounce
                  if (value.length >= 2) {
                    setSearching(true); // Show spinner immediately
                  }
                }}
                autoFocus
              />
              {searching && <span className="search-spinner">{'\u{1F504}'}</span>}
            </div>

            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <div className="search-hint" style={{padding: '10px', color: '#999', fontSize: '14px'}}>
                Type at least 2 characters to search...
              </div>
            )}

            {/* Show "no results" message */}
            {searchQuery.length >= 2 && searchResults.length === 0 && !searching && (
              <div className="search-empty">
                <p>{'\u{1F50D}'} No students found matching "{searchQuery}"</p>
                <p style={{fontSize: '12px', color: '#999', marginTop: '8px'}}>
                  Try typing the full name or account number
                </p>
              </div>
            )}

            {/* Show results - simplified condition */}
            {searchResults.length > 0 && (
              <div className="search-results" style={{marginTop: '10px'}}>
                <div style={{padding: '10px', background: '#e8f5e9', fontSize: '12px', color: '#2e7d32'}}>
                  {'\u2705'} Found {searchResults.length} student(s)
                </div>
                {searchResults.map((result) => (
                  <div
                    key={result.id}
                    className="search-result-item"
                    onClick={() => selectStudent(result.id)}
                  >
                    <div className="result-info">
                      <div className="result-name">{result.studentName}</div>
                      <div className="result-details">
                        {result.accountNumber}  -  {result.schoolName || "No school"}
                      </div>
                    </div>
                    <div className="result-owing">
                      R{((result.termFeesOwing || 0) + (result.adminFeeOwing || 0)).toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!student) {
    return (
      <div className="quick-payment-container">
        <div className="empty-state">
          <p>No student selected</p>
        </div>
      </div>
    );
  }

  return (
    <div className="quick-payment-container">
      {showSuccess && (
        <div className="payment-success-toast">
          {'\u2705'} Payment recorded successfully!
        </div>
      )}

      <div className="quick-payment-form">
        <div className="form-header">
          <div>
            <h1>{'\u{1F4B5}'} Quick Payment</h1>
            <p className="header-subtitle">Record payment for {student.studentName}</p>
          </div>
        </div>

        <div className="student-info-card">
          <div className="info-row">
            <div className="info-item">
              <label>{'\u{1F464}'} Student:</label>
              <span className="value-large">{student.studentName}</span>
            </div>
            <div className="info-item">
              <label>{'\u{1F4CB}'} Account #:</label>
              <span>{student.accountNumber || 'Not Set'}</span>
            </div>
          </div>
          <div className="info-row">
            <div className="info-item">
              <label>{'\u{1F3EB}'} School:</label>
              <span>{student.schoolName}</span>
            </div>
            <div className="info-item">
              <label>{'\u{1F4C5}'} Last Payment:</label>
              <span>{formatDate(student.lastPaymentDate)}</span>
            </div>
          </div>
          <div className="info-row balance-row">
            <div className="info-item">
              <label>{'\u{1F4B8}'} Total Owing:</label>
              <span className="owing-amount">{formatCurrency(student.totalOwing)}</span>
            </div>
            <div className="info-item">
              <label>{'\u2705'} Total Paid:</label>
              <span className="paid-amount">{formatCurrency(student.termFeesPaid + student.adminFeePaid)}</span>
            </div>
          </div>
        </div>

        <form onSubmit={handleSaveAndFinish}>
          <div className="payment-table-wrapper">
            <table className="payment-table">
              <thead>
                <tr>
                  <th>Student Name</th>
                  <th>Total Outstanding</th>
                  <th>Admin Fee Owing</th>
                  <th>Term Fee Owing</th>
                  <th>Admin Fee Payment</th>
                  <th>Term Fee Payment</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><strong>{student.studentName}</strong></td>
                  <td className="amount-cell owing">
                    {formatCurrency(student.totalOwing)}
                  </td>
                  <td className="amount-cell owing">
                    {formatCurrency(student.adminFeeOwing || 0)}
                  </td>
                  <td className="amount-cell owing">
                    {formatCurrency(student.termFeesOwing || 0)}
                  </td>
                  <td>
                    <input
                      type="number"
                      name="adminFeeAmount"
                      value={paymentData.adminFeeAmount}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      max={student.adminFeeOwing || 0}
                      className="amount-input"
                      placeholder={`Max: ${formatCurrency(student.adminFeeOwing || 0)}`}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      name="termFeeAmount"
                      value={paymentData.termFeeAmount}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      className="amount-input"
                      placeholder="Enter amount"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="payment-details">
            <div className="form-row">
              <div className="form-group">
                <label>{'\u{1F4C5}'} Payment Date</label>
                <input
                  type="date"
                  name="paymentDate"
                  value={paymentData.paymentDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group">
                <label>{'\u{1F4B3}'} Payment Method</label>
                <select
                  name="paymentMethod"
                  value={paymentData.paymentMethod}
                  onChange={handleChange}
                  required
                >
                  <option value="EFT">EFT</option>
                  <option value="Cash">Cash</option>
                  <option value="Card">Card</option>
                  <option value="Cheque">Cheque</option>
                </select>
              </div>

              <div className="form-group">
                <label>{'\u{1F4DD}'} Reference (Optional)</label>
                <input
                  type="text"
                  name="reference"
                  value={paymentData.reference}
                  onChange={handleChange}
                  placeholder="Payment reference"
                />
              </div>
            </div>
          </div>

          {getTotalPayment() > 0 && (
            <div className="balance-preview">
              <h4>{'\u{1F4B0}'} Payment Summary</h4>
              <div className="balance-grid">
                <div className="balance-item">
                  <span className="label">Total Payment</span>
                  <span className="value paid">{formatCurrency(getTotalPayment())}</span>
                </div>
                <div className="balance-item">
                  <span className="label">Current Owing</span>
                  <span className="value owing">{formatCurrency(student.totalOwing)}</span>
                </div>
                <div className="balance-item">
                  <span className="label">New Balance</span>
                  <span className={`value ${getNewBalance() > 0 ? 'owing' : 'paid'}`}>
                    {formatCurrency(getNewBalance())}
                  </span>
                </div>
                <div className="balance-item">
                  <span className="label">Status</span>
                  <span className={`status-badge ${getNewBalance() <= 0 ? 'paid' : getNewBalance() < student.totalOwing ? 'partial' : 'outstanding'}`}>
                    {getNewBalance() <= 0 ? 'Paid in Full' : getNewBalance() < student.totalOwing ? 'Partial Payment' : 'Outstanding'}
                  </span>
                </div>
              </div>
            </div>
          )}

          <button
            type="submit"
            disabled={saving || getTotalPayment() <= 0}
            className="btn-save-payment"
          >
            {saving ? `${'\u{1F4BE}'} Saving...` : `${'\u{1F4BE}'} Save Payment`}
          </button>
        </form>
      </div>
    </div>
  );
}

export default QuickPayment;